import { useTeam } from 'app/frontend/base/config/context/team'
import groupBy from 'lodash/groupBy'
import { useMemo } from 'react'

export const useMembersByRole = (members: wowaudit.TeamMembership[]) => {
  const mains = useMemo(() => {
    return members?.filter((m) => !m.teamRank.for_alts)
  }, [members])

  const mainsByRole = useMemo(() => {
    return groupBy(
      mains.sort((a, b) => {
        const classCompare = a.characterReference.characterClass.localeCompare(
          b.characterReference.characterClass,
        )
        if (classCompare !== 0) return classCompare
        return a.characterReference.name.localeCompare(
          b.characterReference.name,
        )
      }),
      (m) => m.role,
    )
  }, [mains])

  const alts = useMemo(() => {
    return members?.filter((m) => m.teamRank.for_alts)
  }, [members])

  const altsByRole = useMemo(() => {
    return groupBy(alts, (m) => m.role)
  }, [alts])

  const membersWithOffspecs = useMemo(() => {
    return (
      members?.flatMap((member) =>
        member.offspecDetails
          .filter((offspec) => offspec.role && offspec.role !== member.role)
          .map((offspec) => ({
            member,
            offspecRole: offspec.role,
          })),
      ) ?? []
    )
  }, [members])

  const offSpecsByRole = useMemo(() => {
    return groupBy(membersWithOffspecs, (item) => item.offspecRole)
  }, [membersWithOffspecs])

  return {
    mains,
    mainsByRole,
    alts,
    altsByRole,
    membersWithOffspecs,
    offSpecsByRole,
  }
}

export const useGroupedTeamMembers = () => {
  const { team } = useTeam()

  const { groupedMembers, userIdsWithDuplicateMains, mainByUser } =
    useMemo(() => {
      const groupByUser = groupBy(
        team.members,
        (m) => m.characterReference.userId?.toString() ?? `no-user-${m.id}`,
      )

      const userIdsWithDuplicateMains: number[] = []
      const groupedMembers: wowaudit.TeamMembership[][] = []
      const mainByUser: Record<string, wowaudit.TeamMembership> = {}

      if (!team.members) return { groupedMembers, userIdsWithDuplicateMains }

      for (const members of Object.values(groupByUser)) {
        const mains = members.filter((m) => !m.teamRank.for_alts)
        const alts = members
          .filter((m) => m.teamRank.for_alts)
          .sort((a, b) => {
            const rankDiff =
              Number(a.teamRank.for_alts) - Number(b.teamRank.for_alts)
            if (rankDiff !== 0) return rankDiff
            return a.characterReference.name.localeCompare(
              b.characterReference.name,
            )
          })

        if (mains.length === 0) {
          groupedMembers.push(alts)
        } else {
          groupedMembers.push([mains[0], ...alts])

          if (mains[0].characterReference.userId) {
            mainByUser[mains[0].characterReference.userId.toString()] = mains[0]
          }

          if (mains.length > 1 && mains[0].characterReference.userId) {
            userIdsWithDuplicateMains.push(mains[0].characterReference.userId)
          }

          mains.slice(1).forEach((main) => groupedMembers.push([main]))
        }
      }

      return {
        groupedMembers: groupedMembers.sort((a, b) =>
          a[0].characterReference.name.localeCompare(
            b[0].characterReference.name,
          ),
        ),
        userIdsWithDuplicateMains,
        mainByUser,
      }
    }, [team.members])

  return { groupedMembers, userIdsWithDuplicateMains, mainByUser }
}
